
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { format, isDate } from 'date-fns';
import produce from 'immer';
import { map } from 'rxjs/operators';

import { CreateNoteGQL, CreateNotesInput, NoteTarget } from 'generated/graphql';

import { getNoteFragmentInfo } from 'app/note/helpers/get-note-fragment-info';
import { getNoteTargetId } from 'app/note/helpers/get-note-target-id';
import { NoteTextType } from 'app/note/helpers/note-types';
import { NOTE_CHANGES } from 'app/note/note.actions';
import { ToastrService } from 'core/toastr.service';
import { getSafe, isNilOrEmpty } from 'helpers';

@Component({
  selector: 'app-create-note',
  templateUrl: './create-note.component.html'
})
export class CreateNoteComponent {
  form: UntypedFormGroup;

  @Input() shouldAutofocusInput = false;
  /**
   * noteType is used to:
   *  determine which html input to display
   *  determine the title / text / labels of ui.
   */
  @Input() noteType?: NoteTextType = 'text';

  @Input() targetId: string;
  @Input() targetType: NoteTarget;

  @Output() noteAdded = new EventEmitter<any>();

  get campaignId() {
    return getSafe(() => this._route.snapshot.queryParams.campaignId, null);
  }

  constructor(
    private _toastrService: ToastrService,
    private _createNoteGQL: CreateNoteGQL,
    private _route: ActivatedRoute,
    private _store: Store,
    formBuilder: UntypedFormBuilder
  ) {
    this.form = formBuilder.group({
      text: [null, [Validators.required]]
    });
  }

  onSave(form) {
    const input = this.prepareMutation();
    this._createNoteGQL.mutate({ input },
      {
        update: (store, { data }) => {
          const newNotes = data.createNotes.notes;
          const { fragment, fragmentName } = getNoteFragmentInfo(this.targetType);

          // Get the CacheId for the parent object containing the notes
          const id = getNoteTargetId(this.targetType, this.targetId, this.campaignId);
          // Item which contains the notes
          const parentItem = store.readFragment({
            id, fragment, fragmentName
          }) as any;

          const nextState = produce(parentItem, draft => {
            if (draft?.notes != null) {
              draft.notes = [...newNotes];
            }
          });

          if (!isNilOrEmpty(parentItem)) {
            store.writeFragment({
              id,
              fragment,
              fragmentName,
              data: nextState
            });
          }
        }
      }).pipe(
        map(({ data }) => data.createNotes.notes)
      )
      .subscribe({
        next: (notes) => {
          form.reset();

          this._store.dispatch(NOTE_CHANGES({
            payload: {
              targetId: this.targetId,
              targetType: this.targetType,
              campaignId: this.campaignId
            }
          }));

          this.noteAdded.emit(notes);
          this._toastrService.success('Note saved');
        },
        error: (err) => this._toastrService.error(err)
      });
  }

  private prepareMutation(): CreateNotesInput {
    return {
      campaignId: this.campaignId,
      targetId: this.targetId,
      targetType: this.targetType,
      notes: [{
        text: this.asNoteTextForGQLInput(this.form.value.text)
      }]
    };
  }

  private asNoteTextForGQLInput(value: any) {
    if (isNilOrEmpty(value) && !isDate(value)) {
      return value;
    }

    if (this.noteType?.toLowerCase() === 'date') {
      return format(value, 'yyyy-MM-dd');
    }

    return value;
  }

}
